import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Controller, useForm } from 'react-hook-form';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Archive, ArchiveRestore, HardHat, MapPin, Pencil, Plus, Trash2 } from 'lucide-react';
import { Button, FormField, Modal, PageHeader, Skeleton, StatusBadge, WilayaSelect } from '@/components/ui';
import { Input } from '@/components/ui/input';
import { getApiErrorMessage } from '@/lib/axios';
import { usePerms } from '@/hooks/usePerms';
import {
  type ChantierDTO,
  type ChantierInput,
  type ChantierStatut,
  PRIORITES,
  TYPES_CHANTIER,
  TYPE_CHANTIER_BADGE,
  TYPE_CHANTIER_LABEL,
  createChantier,
  deleteChantier,
  listChantiers,
  updateChantier,
} from '@/services/chantiers';
import { listTiers } from '@/services/tiers';
import { toast } from '@/store/toastStore';
import { CHANTIER_STATUT } from './statuts';

const STATUTS: ChantierStatut[] = ['EN_PREPARATION', 'EN_COURS', 'SUSPENDU', 'ACHEVE'];
const PRIORITE_LABEL: Record<string, string> = { BASSE: 'Basse', NORMALE: 'Normale', HAUTE: 'Haute', CRITIQUE: 'Critique' };

const toForm = (c: ChantierDTO): ChantierInput => ({
  code: c.code, nom: c.nom, type_chantier: c.type_chantier, numero_marche: c.numero_marche,
  client_id: c.client_id, conducteur_travaux: c.conducteur_travaux, localisation: c.localisation, wilaya_id: c.wilaya_id,
  priorite: c.priorite, surface_m2: c.surface_m2, lineaire_ml: c.lineaire_ml, statut: c.statut, taux_avancement: c.taux_avancement,
  date_debut_prevue: c.date_debut_prevue, date_debut_reelle: c.date_debut_reelle, date_fin_prevue: c.date_fin_prevue,
  delai_contractuel_jours: c.delai_contractuel_jours, notes: c.notes,
});

export function ChantiersPage() {
  const perms = usePerms('chantiers');
  const queryClient = useQueryClient();
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState<ChantierDTO | null>(null);
  const [showArchived, setShowArchived] = useState(false);

  const { data, isLoading } = useQuery({ queryKey: ['chantiers', { showArchived }], queryFn: () => listChantiers({ archives: showArchived }) });
  const { data: clients } = useQuery({ queryKey: ['tiers', 'clients'], queryFn: () => listTiers({ type: 'CLIENT', size: 200 }), enabled: modalOpen });

  const { register, handleSubmit, reset, control, watch, formState: { errors } } = useForm<ChantierInput>({
    defaultValues: { statut: 'EN_PREPARATION', taux_avancement: 0, wilaya_id: null, priorite: 'NORMALE' },
  });
  // Type = Hydraulique → on saisit le linéaire (ml) plutôt que la surface (m²).
  const typeChantier = watch('type_chantier');

  const invalidate = () => queryClient.invalidateQueries({ queryKey: ['chantiers'] });
  const openCreate = () => { setEditing(null); reset({ statut: 'EN_PREPARATION', taux_avancement: 0, wilaya_id: null, priorite: 'NORMALE', code: '', nom: '' }); setModalOpen(true); };
  const openEdit = (c: ChantierDTO) => { setEditing(c); reset(toForm(c)); setModalOpen(true); };

  const saveMutation = useMutation({
    mutationFn: (values: ChantierInput) => {
      // budget_materiaux retiré du formulaire terrain (champ financier) — géré côté Contrôle des coûts.
      const payload = {
        ...values,
        taux_avancement: Number(values.taux_avancement),
        client_id: values.client_id || null,
        type_chantier: values.type_chantier || null,
        numero_marche: values.numero_marche || null,
        conducteur_travaux: values.conducteur_travaux || null,
        date_debut_reelle: values.date_debut_reelle || null,
        surface_m2: values.surface_m2 ? Number(values.surface_m2) : null,
        lineaire_ml: values.lineaire_ml ? Number(values.lineaire_ml) : null,
        delai_contractuel_jours: values.delai_contractuel_jours ? Number(values.delai_contractuel_jours) : null,
      };
      return editing ? updateChantier(editing.id, payload) : createChantier(payload);
    },
    onSuccess: () => { invalidate(); toast.success(editing ? 'Chantier modifié.' : 'Chantier créé.'); reset(); setModalOpen(false); },
    onError: (e) => toast.error(getApiErrorMessage(e)),
  });
  const archiveMutation = useMutation({
    mutationFn: ({ id, archived }: { id: string; archived: boolean }) => updateChantier(id, { is_archived: archived }),
    onSuccess: (_d, v) => { invalidate(); toast.success(v.archived ? 'Chantier archivé.' : 'Chantier restauré.'); },
    onError: (e) => toast.error(getApiErrorMessage(e)),
  });
  const deleteMutation = useMutation({
    mutationFn: (id: string) => deleteChantier(id),
    onSuccess: () => { invalidate(); toast.success('Chantier supprimé.'); },
    onError: (e) => toast.error(getApiErrorMessage(e)),
  });

  const act = (e: React.MouseEvent, fn: () => void) => { e.preventDefault(); e.stopPropagation(); fn(); };

  return (
    <>
      <PageHeader
        title="Chantiers"
        subtitle="Suivi opérationnel des chantiers (le chantier fait office de projet)"
        breadcrumbs={[{ label: 'Advanced Works ERP' }, { label: 'Chantiers' }]}
        actions={
          <div className="flex items-center gap-3">
            <label className="flex cursor-pointer items-center gap-2 text-sm text-content-secondary">
              <input type="checkbox" className="size-4 accent-brand-primary" checked={showArchived} onChange={(e) => setShowArchived(e.target.checked)} />
              Archivés
            </label>
            {perms.canAjouter && <Button onClick={openCreate}><Plus /> Nouveau chantier</Button>}
          </div>
        }
      />

      {isLoading ? (
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {Array.from({ length: 6 }).map((_, i) => <Skeleton key={i} className="h-56 w-full rounded-lg" />)}
        </div>
      ) : data && data.items.length > 0 ? (
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {data.items.map((c) => {
            const st = CHANTIER_STATUT[c.statut];
            return (
              <Link
                key={c.id}
                to={`/chantiers/${c.id}`}
                className="group flex flex-col overflow-hidden rounded-lg border border-border bg-surface-secondary shadow-sm transition-all hover:-translate-y-0.5 hover:shadow-md"
              >
                <div className="flex h-24 items-center justify-center bg-gradient-to-br from-sidebar-bg to-sidebar-hover">
                  <HardHat className="size-9 text-white/80" />
                </div>
                <div className="flex flex-1 flex-col gap-3 p-4">
                  <div className="flex items-start justify-between gap-2">
                    <div className="min-w-0">
                      <div className="flex items-center gap-1.5">
                        <p className="text-xs font-semibold text-content-muted">{c.code}</p>
                        {c.type_chantier && (
                          <span className="rounded bg-surface-hover px-1.5 py-px text-[10px] font-semibold uppercase tracking-wide text-content-muted">
                            {TYPE_CHANTIER_BADGE[c.type_chantier]}
                          </span>
                        )}
                      </div>
                      <h3 className="truncate font-bold text-content-primary group-hover:text-brand-primary">{c.nom}</h3>
                    </div>
                    <StatusBadge status={st.status} label={st.label} />
                  </div>
                  {c.localisation && <p className="flex items-center gap-1.5 text-xs text-content-secondary"><MapPin className="size-3.5" /> {c.localisation}</p>}
                  <div>
                    <div className="mb-1 flex items-center justify-between text-xs">
                      <span className="text-content-muted">Avancement</span>
                      <span className="font-semibold tabular-nums text-content-primary">{Number(c.taux_avancement).toFixed(0)} %</span>
                    </div>
                    <div className="h-2 overflow-hidden rounded-full bg-surface-hover">
                      <div className="h-full rounded-full bg-brand-primary" style={{ width: `${c.taux_avancement}%` }} />
                    </div>
                  </div>
                  {/* Actions */}
                  <div className="mt-1 flex items-center justify-end gap-1 border-t border-border pt-2">
                    {showArchived ? (
                      perms.canArchiver && <button onClick={(e) => act(e, () => archiveMutation.mutate({ id: c.id, archived: false }))} className="rounded p-1.5 text-content-muted hover:bg-surface-hover hover:text-status-done" aria-label="Restaurer"><ArchiveRestore className="size-4" /></button>
                    ) : (
                      <>
                        {perms.canModifier && <button onClick={(e) => act(e, () => openEdit(c))} className="rounded p-1.5 text-content-muted hover:bg-surface-hover hover:text-brand-primary" aria-label="Modifier"><Pencil className="size-4" /></button>}
                        {perms.canArchiver && <button onClick={(e) => act(e, () => archiveMutation.mutate({ id: c.id, archived: true }))} className="rounded p-1.5 text-content-muted hover:bg-surface-hover hover:text-content-primary" aria-label="Archiver"><Archive className="size-4" /></button>}
                      </>
                    )}
                    {perms.canSupprimer && <button onClick={(e) => act(e, () => { if (window.confirm(`Supprimer le chantier « ${c.nom} » ?`)) deleteMutation.mutate(c.id); })} className="rounded p-1.5 text-content-muted hover:bg-status-stuck/10 hover:text-status-stuck" aria-label="Supprimer"><Trash2 className="size-4" /></button>}
                  </div>
                </div>
              </Link>
            );
          })}
        </div>
      ) : (
        <div className="flex min-h-[40vh] flex-col items-center justify-center rounded-lg border border-dashed border-border bg-surface-secondary text-center">
          <HardHat className="size-10 text-content-muted" />
          <p className="mt-3 text-sm text-content-secondary">{showArchived ? 'Aucun chantier archivé.' : 'Aucun chantier. Créez votre premier chantier.'}</p>
          {!showArchived && <Button className="mt-4" onClick={openCreate}><Plus /> Nouveau chantier</Button>}
        </div>
      )}

      <Modal
        open={modalOpen}
        onOpenChange={setModalOpen}
        title={editing ? 'Modifier le chantier' : 'Nouveau chantier'}
        footer={
          <>
            <Button variant="secondary" onClick={() => setModalOpen(false)}>Annuler</Button>
            <Button onClick={handleSubmit((v) => saveMutation.mutate(v))} loading={saveMutation.isPending}>{editing ? 'Enregistrer' : 'Créer'}</Button>
          </>
        }
      >
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <FormField label="Code"><Input placeholder="Auto (CH-001…)" {...register('code')} disabled={!!editing} /></FormField>
          <FormField label="Statut">
            <select {...register('statut')} className="flex h-10 w-full rounded-md border border-border bg-surface-secondary px-3 text-sm">
              {STATUTS.map((s) => <option key={s} value={s}>{CHANTIER_STATUT[s].label}</option>)}
            </select>
          </FormField>
          <FormField label="Nom" required className="sm:col-span-2"><Input placeholder="ex : Évitement RN11 Oran" {...register('nom')} /></FormField>
          {/* TYPE — obligatoire, première position après le nom (choix forcé). */}
          <FormField label="Type" required>
            <select {...register('type_chantier', { required: 'Sélectionnez le type de chantier.' })} className="flex h-10 w-full rounded-md border border-border bg-surface-secondary px-3 text-sm">
              <option value="">— Choisir le type —</option>
              {TYPES_CHANTIER.map((t) => <option key={t} value={t}>{TYPE_CHANTIER_LABEL[t]}</option>)}
            </select>
            {errors.type_chantier && <p className="mt-1 text-xs text-status-stuck">{errors.type_chantier.message}</p>}
          </FormField>
          <FormField label="Client (si connu)">
            <select {...register('client_id')} className="flex h-10 w-full rounded-md border border-border bg-surface-secondary px-3 text-sm">
              <option value="">— Aucun / inconnu —</option>
              {clients?.items.map((c) => <option key={c.id} value={c.id}>{c.code} · {c.raison_sociale}</option>)}
            </select>
          </FormField>
          <FormField label="Priorité">
            <select {...register('priorite')} className="flex h-10 w-full rounded-md border border-border bg-surface-secondary px-3 text-sm">
              {PRIORITES.map((p) => <option key={p} value={p}>{PRIORITE_LABEL[p]}</option>)}
            </select>
          </FormField>
          <FormField label="Conducteur de travaux"><Input placeholder="Responsable AWC sur site" {...register('conducteur_travaux')} /></FormField>
          <FormField label="N° marché"><Input placeholder="Contrat maître d'ouvrage" {...register('numero_marche')} /></FormField>
          <FormField label="Localisation"><Input {...register('localisation')} /></FormField>
          <FormField label="Wilaya">
            <Controller control={control} name="wilaya_id" render={({ field }) => <WilayaSelect value={field.value} onChange={field.onChange} />} />
          </FormField>
          {/* Surface (m²) OU linéaire (ml) : l'hydraulique se mesure en linéaire. */}
          {typeChantier === 'HYDRAULIQUE' ? (
            <FormField label="Linéaire (ml) — si applicable"><Input type="number" step="1" {...register('lineaire_ml')} /></FormField>
          ) : (
            <FormField label="Surface (m²) — si applicable"><Input type="number" step="1" {...register('surface_m2')} /></FormField>
          )}
          <FormField label="Délai contractuel (jours)"><Input type="number" min="0" step="1" {...register('delai_contractuel_jours')} /></FormField>
          <FormField label="Avancement (%)"><Input type="number" min="0" max="100" step="1" {...register('taux_avancement')} /></FormField>
          <FormField label="Date début prévue"><Input type="date" {...register('date_debut_prevue')} /></FormField>
          <FormField label="Date démarrage réelle"><Input type="date" {...register('date_debut_reelle')} /></FormField>
          <FormField label="Date fin prévue" className="sm:col-span-2"><Input type="date" {...register('date_fin_prevue')} /></FormField>
        </div>
      </Modal>
    </>
  );
}
